import type { Dispatch, SetStateAction } from 'react';
import { fetchOrderDetails } from '../../../services/shipStationAPI';
import { fetchPackingLogSubmitted } from '../../../services/packingLogAPI';
import { fetchOrderShelfLocations, fetchPrefixMap } from '../../../services/skuShelfIndexAPI';
import { fetchSkusWithNotes } from '../../../services/productWarehouseNotesAPI';
import { hydrateSharedOrderScans } from '../../../services/orderPickScanAPI';
import type { BundleBinScanMap } from '../../../utils/scanSessionCache';
import { isInternationalShipTo } from '@/shared/utils/shipToCountry';
import type { BundleData, OrderItem } from '../../../types/apiTypes';
import {
  extractBundleData,
  getBundleDataOptionsProperty,
  maxComponentQuantity,
  prepareItemListCore,
  type RawOrderItem,
} from './orderDataLogic';
import { applyShelfMapToOrderItems, getShelfLookupSkus, loadOrderShelfLocationMap } from './shelfUtils';

export interface OrderDetailContent {
  readonly shipToName: string;
  readonly isInternational: boolean;
  readonly orderNumber: string;
  readonly title: string;
  readonly tagIds: number[];
  readonly items: OrderItem[];
  readonly bins: BundleBinScanMap;
  readonly prefixToSkuMap: Record<string, string>;
  readonly skusWithNotes: Record<string, true>;
  readonly submitted: boolean;
  readonly scanMessage: string;
  readonly shelfLocationsLoaded: boolean;
}

interface PrefetchEntry {
  readonly orderId: string;
  readonly promise: Promise<OrderDetailContent>;
  result: OrderDetailContent | null;
  failed: boolean;
}

let entry: PrefetchEntry | null = null;

function rawOrderItems(items: ReadonlyArray<{
  sku?: string;
  name?: string;
  quantity?: number;
  options?: ReadonlyArray<{ name?: string; value?: string }>;
}> | undefined): RawOrderItem[] {
  if (!items) return [];
  return items.map((item) => ({
    sku: item.sku,
    name: item.name,
    quantity: item.quantity,
    options: item.options ? item.options.map((option) => ({ name: option.name, value: option.value })) : undefined,
  }));
}

export async function loadOrderDetailContent(orderId: string): Promise<OrderDetailContent> {
  const data = await fetchOrderDetails(orderId);
  const shipToName = data.shipTo?.name ?? '';
  const orderNumber = String(data.orderNumber ?? '');
  let bundleDataCache: Record<string, BundleData> = {};
  const setBundleDataCache: Dispatch<SetStateAction<Record<string, BundleData>>> = (update) => {
    bundleDataCache = typeof update === 'function' ? update(bundleDataCache) : update;
  };
  const itemsWithComponents = await prepareItemListCore(
    rawOrderItems(data.items),
    bundleDataCache,
    setBundleDataCache,
    (item) => extractBundleData(item, getBundleDataOptionsProperty),
    maxComponentQuantity,
  );
  const uniqueSkus = getShelfLookupSkus(itemsWithComponents);
  const [shelfResult, prefixToSkuMap, skusWithNotes, submitted] = await Promise.all([
    loadOrderShelfLocationMap(uniqueSkus, fetchOrderShelfLocations),
    fetchPrefixMap(uniqueSkus),
    fetchSkusWithNotes(uniqueSkus),
    fetchPackingLogSubmitted(orderNumber),
  ]);
  const itemsWithShelfData = applyShelfMapToOrderItems(
    itemsWithComponents,
    shelfResult.map,
    shelfResult.loaded,
  );

  let items = itemsWithShelfData;
  let bins: BundleBinScanMap = {};
  let scanMessage = '';
  try {
    const shared = await hydrateSharedOrderScans(orderId, itemsWithShelfData);
    items = shared.items;
    bins = shared.bins;
  } catch (scanError: unknown) {
    console.error('Error loading shared scans:', scanError);
    scanMessage = 'Failed to load shared scans.';
  }

  return {
    shipToName,
    isInternational: isInternationalShipTo(data.shipTo),
    orderNumber,
    title: `${shipToName}\n${data.orderNumber}`,
    tagIds: data.tagIds ? [...data.tagIds] : [],
    items,
    bins,
    prefixToSkuMap,
    skusWithNotes,
    submitted,
    scanMessage,
    shelfLocationsLoaded: shelfResult.loaded,
  };
}

/** Start the order-detail reads while the pick-next label scanner is open. */
export function prefetchOrderDetail(orderId: string | number): void {
  const key = String(orderId);
  if (!key) return;
  if (entry?.orderId === key && !entry.failed) return;
  const next: PrefetchEntry = {
    orderId: key,
    promise: loadOrderDetailContent(key),
    result: null,
    failed: false,
  };
  next.promise.then(
    (result) => {
      if (entry === next) entry.result = result;
    },
    (error: unknown) => {
      console.error('Error prefetching order details:', error);
      if (entry === next) entry.failed = true;
    },
  );
  entry = next;
}

/** Resolved payload for this order, removed so a later visit loads fresh. */
export function takeResolvedOrderDetailPrefetch(orderId: string): OrderDetailContent | null {
  if (entry == null || entry.orderId !== orderId || entry.result == null) {
    return null;
  }
  const result = entry.result;
  entry = null;
  return result;
}

/** In-flight prefetch for this order. Null when missing or already failed. */
export function peekOrderDetailPrefetch(orderId: string): Promise<OrderDetailContent> | null {
  if (entry == null || entry.orderId !== orderId || entry.failed) {
    return null;
  }
  return entry.promise;
}

export function dropOrderDetailPrefetch(orderId: string | number): void {
  if (entry?.orderId === String(orderId)) entry = null;
}
